import { ScheduleType } from '@services/api/deckManagement/graphql/schema/API';
import { z } from 'zod';

import {
  BaseDeckManagementEntry,
  createChangeSetOperations,
  nullableBoolean,
  nullableNumber,
  nullableString,
} from './Common.schema';

/**
 * Schema for TOC entries.
 * Extends BaseDeckManagementEntry to ensure consistent pk/sk handling.
 * Base schema for TOCEntry that matches the API type structure.
 */
export const TOCEntrySchema = BaseDeckManagementEntry.extend({
  // TODO: Currently using defined basic types. Change these into more specific validations in the future.
  toc_id: z.string(),
  page_id: nullableString,
  page_no: nullableString,
  toc_page_name: nullableString,
  page_name: nullableString,
  is_visible: nullableBoolean,
  is_active: nullableBoolean,
  group_name: nullableString,
  serial_no: nullableNumber,
  created_date: nullableString,
  last_updated_date: nullableString,
  schedule_type: z.enum(ScheduleType).nullable().optional(),
});

/**
 * Schema for creating new TOC entries.
 * Omits pk and sk fields as these are generated by the service.
 * Matches TOCItemsInput GraphQL type requirements.
 */
export const CreateTOCEntrySchema = TOCEntrySchema.omit({
  pk: true,
  sk: true,
  page_id: true,
  created_date: true,
  last_updated_date: true,
}).extend({
  is_active: z.boolean(), // Required for TOCItemsInput
});

/**
 * Schema for updating existing TOC entries.
 * Omits pk and sk fields as these are generated by the service.
 * Matches TOCItemsInput GraphQL type requirements.
 */
export const UpdateTOCEntrySchema = TOCEntrySchema.omit({
  pk: true,
  sk: true,
  created_date: true,
  last_updated_date: true,
}).extend({
  is_active: z.boolean(), // Required for TOCItemsInput
});

/**
 * Schema for deleting existing TOC entries
 * Includes only the primary fields needed for delete
 */
export const DeleteTOCEntrySchema = z.object({
  toc_id: z.string(),
});

/**
 * Complete changeset schema for TOC operations.
 * Provides create, update, and delete arrays with appropriate validation.
 */
export const TOCChangeSetSchema = createChangeSetOperations({
  create: CreateTOCEntrySchema,
  update: UpdateTOCEntrySchema,
  delete: DeleteTOCEntrySchema,
});

/**
 * Type definitions for use in components and services
 */

/**
 * Type for TOC entries in create operations (without pk/sk)
 */
export type CreateTOCEntryType = z.infer<typeof CreateTOCEntrySchema>;

/**
 * Type for TOC entries in udpate operation (without pk/sk, or last_updated_date)
 */
export type UpdateTOCEntryType = z.infer<typeof UpdateTOCEntrySchema>;

/**
 * Type for TOC entries in update operations (with all fields)
 */
export type TOCEntryType = z.infer<typeof TOCEntrySchema>;

/**
 * Type for the complete TOC changeset structure
 */
export type TOCChangeSetType = z.infer<typeof TOCChangeSetSchema>;
